import { useMemo, useState } from "react";
import { stackedBarChartLayout, evenlySpacedIndices } from "@/lib/chart-math";
import type { DashSeriesBucket } from "@/api/dashboard";

// Feste, wiedererkennbare Kanalfarbe — gilt projektweit (Legenden auf anderen Seiten
// importieren diese Konstante, statt eigene Farben zu erfinden).
//
// Vollstaendigkeit (Fix-Runde 1): die Backend-Funktion buildChannelList() in
// src/modules/dashboard/service.ts ist die EINZIGE Quelle moeglicher Kanal-Keys. Sie liefert
// je nach Aufruf-Kombination aus CHANNELS (platform-map.ts) + AMAZON_SUBS + optionalem
// 'Nicht zugeordnet'-Extra-Kanal folgende Vereinigungsmenge (10 Keys):
//   CHANNELS:      Amazon, eBay, Shopify, Metro, Kaufland, Sonstige
//   AMAZON_SUBS:   Amazon/Shop, Amazon/FBA, Amazon/Business (nur drill=true, ersetzen
//                  'Amazon' NICHT vollstaendig — eine Amazon-Zeile ohne erkennbaren
//                  Sub-Wert faellt im Drilldown weiterhin auf den bloßen Key 'Amazon'
//                  zurueck, siehe buildChannelList()-Kommentar im Backend)
//   Extra:         'Nicht zugeordnet' (nur units/abgang_ek — Positionen, deren Bruecke
//                  zum Grundartikel nicht aufloesbar war; siehe buildLineSeriesSql())
//
// Amazon/Shop, Amazon/FBA, Amazon/Business bekommen bewusst denselben Orange-Farbton wie
// 'Amazon' (Familienzugehoerigkeit), gestuft nur ueber die Helligkeit (heller -> dunkler:
// Shop -> FBA -> Business), damit sie sich auch fuer Rot-Gruen-Farbsehschwaeche (nicht nur
// ueber den Farbton) unterscheiden lassen — Menschen mit Deuteranopie/Protanopie verlieren
// vor allem Rot-Gruen-Kontraste, Helligkeitsunterschiede bleiben erhalten.
//
// 'Nicht zugeordnet' bekommt bewusst einen gedeckten Violett-Grauton (statt eines weiteren
// kraeftigen "Marken"-Tons) — Zweck ist eine Datenluecke zu signalisieren, kein weiterer
// Verkaufskanal. Der Farbton unterscheidet sich klar von "Sonstige" (neutrales Kuehlgrau),
// bleibt aber gedaempft/entsaettigt statt bunt.
//
// Verifikation (Node-Skript, CIE-Lab-ΔE + Deuteranopie-/Protanopie-Simulation nach Machado
// et al. 2009 + WCAG-Kontrast gegen --bg hell (#ffffff) und dunkel (#0f0f0f)):
// - alle 10 Werte sind paarweise eindeutig
// - kleinster ΔE-Abstand normalsichtig: 13.4 (Sonstige/Nicht zugeordnet)
// - kleinster ΔE-Abstand unter simulierter Deuteranopie/Protanopie fuer PAARE MIT
//   MINDESTENS EINER NEUEN FARBE: 7.9 bzw. 11.5 (Amazon/Metro bleiben mit ΔE 2.6 der
//   bekannte, nicht behebbare Schwachpunkt der BESTEHENDEN 6er-Palette — hier nicht
//   angetastet, s.u. "Behalte die Rueckfallfarbe bei")
// - jede neue Farbe hat WCAG-Kontrast >= 1.87 sowohl gegen Weiss als auch gegen
//   Dunkel-Theme-Hintergrund (keine der vier verschwindet in einem der beiden Themes)
// Zusaetzliche Absicherung: jedes Segment ist im Tooltip/aria-label immer zusaetzlich als
// Klartext benannt — Farbe ist nie die einzige Unterscheidungsquelle.
export const CHANNEL_COLORS: Record<string, string> = {
  Amazon: "#ff9900",
  eBay: "#0064d2",
  Shopify: "#95bf47",
  Metro: "#eab308",
  Kaufland: "#e2001a",
  Sonstige: "#6b7280",
  "Amazon/Shop": "#eeb268",
  "Amazon/FBA": "#cf7c17",
  "Amazon/Business": "#935810",
  "Nicht zugeordnet": "#5b556d",
};

const FALLBACK_CHANNEL_COLOR = CHANNEL_COLORS.Sonstige!;

// Gutschriften/negative Werte werden unabhaengig vom Kanal in Coral unterhalb der
// Nulllinie dargestellt — das ist die Signalfarbe fuer "Geld geht raus", nicht die
// Kanal-Identitaet. Kanalfarben oben bleiben fuer Legenden auf anderen Seiten reserviert.
export const NEGATIVE_SEGMENT_COLOR = "var(--coral)";

function segmentColor(channel: string, value: number, colors: Record<string, string>): string {
  if (value < 0) return NEGATIVE_SEGMENT_COLOR;
  return colors[channel] ?? FALLBACK_CHANNEL_COLOR;
}

interface StackedBarChartProps {
  buckets: DashSeriesBucket[];
  channels: string[];
  colors?: Record<string, string>;
  height?: number;
  formatValue?: (n: number) => string;
}

const PADDING = { t: 14, r: 8, b: 26, l: 46 };
const VIEWBOX_WIDTH = 640;
const TOOLTIP_FONT_SIZE = 10;
const TOOLTIP_CHAR_WIDTH = TOOLTIP_FONT_SIZE * 0.6; // JetBrains Mono ist monospace
const TOOLTIP_ROW_H = 14;
const TOOLTIP_PAD_X = 8;

const defaultFormatValue = (n: number) => n.toLocaleString("de-DE");

export function StackedBarChart({
  buckets,
  channels,
  colors = CHANNEL_COLORS,
  height = 240,
  formatValue = defaultFormatValue,
}: StackedBarChartProps) {
  const [hover, setHover] = useState(-1);

  const layout = useMemo(
    () => stackedBarChartLayout(buckets, channels, { width: VIEWBOX_WIDTH, height, padding: PADDING }),
    [buckets, channels, height],
  );

  const labels = buckets.map((b) => b.label);
  const tickIndices =
    labels.length > 0 ? evenlySpacedIndices(labels.length, 6) : [];

  const summary = useMemo(() => {
    if (buckets.length === 0) return "Keine Daten fuer den gewaehlten Zeitraum.";
    const net = layout.bars.reduce((s, b) => s + b.positiveTotal + b.negativeTotal, 0);
    return `Gestapeltes Balkendiagramm mit ${buckets.length} Zeitabschnitten (${labels[0]} bis ${labels[labels.length - 1]}), aufgeschluesselt nach ${channels.length} Kanaelen (${channels.join(", ")}). Summe ueber den Zeitraum: ${formatValue(net)}.`;
  }, [buckets.length, labels, channels, layout.bars, formatValue]);

  if (buckets.length === 0) {
    return (
      <div
        role="img"
        aria-label="Gestapeltes Balkendiagramm, keine Daten fuer den gewaehlten Zeitraum."
        style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <span style={{ fontSize: 12, color: "var(--muted)", fontFamily: "var(--b-font)" }}>
          Keine Daten fuer diesen Zeitraum.
        </span>
      </div>
    );
  }

  const hoverBar = hover >= 0 ? layout.bars[hover] : undefined;

  return (
    <div style={{ position: "relative" }}>
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${height}`}
        width="100%"
        height={height}
        style={{ display: "block" }}
        role="img"
        aria-label={summary}
      >
        {layout.yTicks.map((v) => {
          const y = layout.zeroY - v * (v >= 0 ? layout.posScale : layout.negScale);
          const isZero = v === 0;
          return (
            <g key={v}>
              <line
                x1={PADDING.l}
                x2={VIEWBOX_WIDTH - PADDING.r}
                y1={y}
                y2={y}
                stroke={isZero ? "var(--line)" : "var(--line-2)"}
                strokeWidth={isZero ? 1.25 : 1}
                strokeDasharray={isZero ? undefined : "2 3"}
              />
              <text
                x={PADDING.l - 8}
                y={y + 3}
                fontSize="10"
                fill="var(--muted)"
                textAnchor="end"
                fontFamily="var(--m-font)"
              >
                {formatValue(v)}
              </text>
            </g>
          );
        })}

        {layout.bars.map((bar) => {
          const isHover = hover === bar.index;
          return (
            <g
              key={bar.index}
              onMouseEnter={() => setHover(bar.index)}
              onMouseLeave={() => setHover(-1)}
              style={{ cursor: bar.segments.length > 0 ? "pointer" : "default" }}
            >
              <rect
                x={bar.x + 2}
                y={PADDING.t}
                width={Math.max(bar.width - 4, 0)}
                height={layout.innerHeight}
                fill="transparent"
              />
              {bar.segments.map((seg) => (
                <rect
                  key={seg.channel}
                  x={bar.x + 2}
                  y={seg.y}
                  width={Math.max(bar.width - 4, 0)}
                  height={Math.max(seg.height, 0)}
                  fill={segmentColor(seg.channel, seg.value, colors)}
                  opacity={isHover ? 1 : 0.9}
                />
              ))}
            </g>
          );
        })}

        {tickIndices.map((i) => {
          const bar = layout.bars[i];
          if (!bar) return null;
          return (
            <text
              key={i}
              x={bar.x + bar.width / 2}
              y={height - 8}
              fontSize="10"
              fill="var(--muted)"
              textAnchor="middle"
              fontFamily="var(--m-font)"
            >
              {bar.label}
            </text>
          );
        })}

        {hoverBar && <BarTooltip bar={hoverBar} colors={colors} formatValue={formatValue} chartWidth={VIEWBOX_WIDTH} />}
      </svg>
      <span className="sr-only">{summary}</span>
    </div>
  );
}

function BarTooltip({
  bar,
  colors,
  formatValue,
  chartWidth,
}: {
  bar: ReturnType<typeof stackedBarChartLayout>["bars"][number];
  colors: Record<string, string>;
  formatValue: (n: number) => string;
  chartWidth: number;
}) {
  const total = bar.positiveTotal + bar.negativeTotal;
  const channelRows = bar.segments.length > 0
    ? bar.segments.map((s) => ({
        channel: s.channel,
        value: s.value,
        color: segmentColor(s.channel, s.value, colors),
        text: `${s.channel}: ${formatValue(s.value)}`,
        bold: false,
      }))
    : [{ channel: "", value: 0, color: "var(--muted)", text: "Keine Daten", bold: false }];
  const rows = bar.segments.length > 0
    ? [{ channel: "", value: total, color: "", text: `Gesamt: ${formatValue(total)}`, bold: true }, ...channelRows]
    : channelRows;

  const longest = Math.max(bar.label.length, ...rows.map((r) => r.text.length));
  const tooltipWidth = Math.min(260, Math.max(90, longest * TOOLTIP_CHAR_WIDTH + TOOLTIP_PAD_X * 2 + 10));
  const tooltipHeight = TOOLTIP_ROW_H * (rows.length + 1) + 10;

  const topSegY = bar.segments.length > 0 ? Math.min(...bar.segments.map((s) => s.y)) : PADDING.t;
  let tx = bar.x + bar.width / 2 - tooltipWidth / 2;
  tx = Math.min(Math.max(tx, PADDING.l), chartWidth - PADDING.r - tooltipWidth);
  let ty = topSegY - tooltipHeight - 8;
  if (ty < 0) ty = topSegY + 8;

  return (
    <g pointerEvents="none">
      <rect x={tx} y={ty} width={tooltipWidth} height={tooltipHeight} rx="4" fill="var(--ink)" opacity="0.96" />
      <text x={tx + TOOLTIP_PAD_X} y={ty + TOOLTIP_ROW_H} fontSize="10" fontWeight="700" fill="#fff" fontFamily="var(--m-font)">
        {bar.label}
      </text>
      {rows.map((r, i) => (
        <g key={`${r.channel}-${i}`}>
          {r.channel && (
            <rect
              x={tx + TOOLTIP_PAD_X}
              y={ty + TOOLTIP_ROW_H * (i + 2) - 8}
              width="7"
              height="7"
              rx="1.5"
              fill={r.color}
            />
          )}
          <text
            x={tx + TOOLTIP_PAD_X + (r.channel ? 12 : 0)}
            y={ty + TOOLTIP_ROW_H * (i + 2)}
            fontSize="10"
            fontWeight={r.bold ? "700" : "400"}
            fill="#fff"
            fontFamily="var(--m-font)"
          >
            {r.text}
          </text>
        </g>
      ))}
    </g>
  );
}
